import { constants } from '../constants';
import { Logger } from './logger';
import {
  IScenario,
  IActivity,
  TStopSignalLogArgs,
  TSignalLogArgs,
  TReturn,
  ISubstrateLogger,
  TWithSignalSyncLogArgs,
  TWithSignalAsyncLogArgs,
} from '../types';

/**
 * Substrate Logger to log events for user behavior analysis
 * Key data entries include
 * Sn: scenario name
 * SceId: scenario id, this is re-generated whenever a scenario is created
 * An: activity name
 * ActId: activity id, this is re-generated when an activity is created
 * Mes: a custom message for the signal
 * Dur: time taken for the signal
 */
export class SubstrateLogger extends Logger implements ISubstrateLogger {
  /**
   * startSignal - used to measure start of a signal
   */
  startSignal(scenario: IScenario, activity: IActivity): void {
    if (!scenario || !activity) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    activity.startTime = new Date();
  }

  /**
   * stopSignal - used to measure stop of an activity
   */
  stopSignal(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [message, api, trackDuration = true]: TStopSignalLogArgs,
  ): void {
    if (!scenario || !activity || (trackDuration && !activity.startTime)) {
      return;
    }

    const endTime = new Date();
    super.log({
      timeStamp: trackDuration ? activity.startTime!.toJSON() : endTime.toJSON(),
      timeTaken: trackDuration ? (endTime.getTime() - activity.startTime!.getTime()) : 0,
      logCategory: constants.LOGCATEGORY.SUBSTRATE,
      logLevel: constants.LOGLEVEL.INFO,
      entryType: constants.ENTRYTYPE.ACTIVITY,
      activity,
      scenario,
      parentScenario,
      message,
      api,
    });
  }

  /**
   * signal - an atomic signal that has start and stop paired automatically
   */
  signal(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [message, api]: TSignalLogArgs,
  ): void {
    this.stopSignal(scenario, activity, parentScenario, [message, api, false]);
  }

  /**
   * withSignalSync - caller provides a function
   * start and stop are measured at the start and stop of the function
   */
  withSignalSync<T>(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [action]: TWithSignalSyncLogArgs<T>,
  ): T {
    this.startSignal(scenario, activity);
    const actionReturn = action();
    this.stopSignal(
      scenario,
      activity,
      parentScenario,
      actionReturn ? [actionReturn.message, actionReturn.api] : [],
    );
    return actionReturn as T;
  }

  /**
   * withSignalAsync - async version of withSignalSync, caller provides a promise
   * start and stop are measured at the start and stop of the promise
   */
  withSignalAsync<T extends TReturn>(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [promise]: TWithSignalAsyncLogArgs<T>,
  ): Promise<T> {
    this.startSignal(scenario, activity);
    const onPromiseDone = (promiseReturn: T) => {
      this.stopSignal(
        scenario,
        activity,
        parentScenario,
        promiseReturn ? [promiseReturn.message, promiseReturn.api] : [],
      );
    };

    promise.then(onPromiseDone, onPromiseDone);
    return promise;
  }

  getLogMethods(): string[] {
    return ['startSignal', 'stopSignal', 'signal', 'withSignalSync', 'withSignalAsync'];
  }
}
